import i18n from 'i18n-react';
import bem from 'easy-bem';

/**
 * Translation hook that fetches translation values from the currently active language.
 * Translations should be component-scoped unless using the `t.general` method.
 * @name    useTranslate
 * @param   {String} component The name of the component
 */
const useTranslate = (component) => {
  if (!component) return () => i18n.translate('dev.translationError');

  /**
   * Returns a `ReactNode` with the selected translation value
   * @name t
   * @param {String} key Translation key to use
   * @param {Object} ctx Values to pass into the translation string
   */
  const t = (key, ctx) => i18n.translate([component, key].join('.'), typeof ctx === 'object' ? ctx : { context: ctx });

  /**
   * Returns a `ReactNode` with the selected translation value, general ignores the provided `component` name
   * and fetches translations from the `general` key instead.
   * @name t.general
   * @param {String} key Translation key to use
   * @param {Object} ctx Values to pass into the translation string
   */
  t.general = (key, ctx) =>
    i18n.translate(['general', key].join('.'), typeof ctx === 'object' ? ctx : { context: ctx });

  /**
   * Returns a `ReactNode` with the selected translation value, error ignores the provided `component` name
   * and fetches translations from the `error` key instead.
   * @name t.error
   * @param {String} key Translation key to use
   * @param {Object} ctx Values to pass into the translation string
   */
  t.error = (key, ctx) => i18n.translate(['error', key].join('.'), typeof ctx === 'object' ? ctx : { context: ctx });

  return t;
};

/**
 * @typedef {Modifiers} {string}
 */

const getParams = (element, modifiers) => {
  const params = { element, modifiers };

  if (typeof modifiers === 'string' || typeof modifiers === 'number') {
    if (modifiers.toString().trim().length === 0) {
      params.modifiers = {};
    } else {
      params.modifiers = { [modifiers]: true };
    }
  }

  if (typeof element === 'object') {
    params.element = 'wrapper';
    params.modifiers = element;
  }

  return Object.values(params);
};

/**
 * Generates BEM-consistent class names.
 * For more information see: [BEM Documentation](https://en.bem.info/methodology/css/).
 *
 * @param {String} block The name of the component
 */
const useClassName = (block) => {
  /**
   * Return an object with a BEM-compliant class name on the `className` key. Result should be spread on a React element.
   * @name c
   * @param {String} element        Element: `block__[element]`
   * @param {Modifiers} modifiers   Modifiers: `block__element--[modifiers]`
   */
  const c = (element = 'wrapper', modifiers = {}) => ({
    className: bem(block)(...getParams(element, modifiers)),
  });

  return c;
};

export { useClassName, useTranslate };
//# sourceMappingURL=index.js.map
